import { Fragment } from "react";

import { MessageFolder } from "@asd/customer-portal-api";
import { formatDateTime, parseTimestamp, ReportKey } from "@asd/customer-portal-ui";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import { ButtonBase } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

export const FOLDERS = Object.values(MessageFolder);
export const FOLDER_ICONS: Record<MessageFolder, React.ReactNode> = {
  [MessageFolder.Inbox]: <InboxOutlinedIcon fontSize="small" />,
  [MessageFolder.Favorites]: <StarBorderOutlinedIcon fontSize="small" />,
  [MessageFolder.Archive]: <ArchiveOutlinedIcon fontSize="small" />,
};

export const toGridModel = (ids: Set<string>) => {
  return { type: "include" as const, ids: new Set(ids) };
};

export const fromGridModel = (model: { type: string; ids: Set<unknown> }, allIds: string[]): Set<string> => {
  if (model.type === "exclude") {
    return new Set(allIds.filter((id) => !model.ids.has(id)));
  }
  return new Set(Array.from(model.ids) as string[]);
};

export const COLUMNS: GridColDef[] = [
  {
    field: "date",
    headerName: "Date",
    width: 180,
    valueFormatter: (value: string) => (value ? formatDateTime(parseTimestamp(value)) : ""),
  },
  {
    field: "subject",
    headerName: "Message",
    flex: 1,
  },
];

const REPORT_NAME_TO_KEY: Record<string, ReportKey> = {
  "Risk Summary": "summary",
  "Trending Over-Usage": "overUsage",
  "Unused Services": "unused",
  "Depleted Services": "depleted",
  "Overtime Usage": "overtime",
};

export const renderMessageBody = (
  body: string,
  onNavigate: (reportKey: ReportKey, monthValue: string) => void,
  onProfileNavigate: () => void,
): React.ReactNode[] => {
  const monthMatch = body.match(/for ([A-Za-z]+ \d{4})/);
  const monthValue = (() => {
    if (!monthMatch) return "";
    const monthLabel = monthMatch[1];
    const [monthName, yearStr] = monthLabel.split(" ");
    const year = parseInt(yearStr, 10);
    const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
    return `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
  })();

  const reportNames = Object.keys(REPORT_NAME_TO_KEY);
  const specialPhrases = ["reset your password"];

  const allPatterns = [...reportNames.map((name) => `\\- ${name}`), ...specialPhrases].join("|");
  const linePattern = new RegExp(`(${allPatterns})`);

  const parts = body.split(linePattern);

  return parts.map((part) => {
    if (part.startsWith("- ")) {
      const trimmed = part.replace(/^- /, "");
      const reportKey = REPORT_NAME_TO_KEY[trimmed];

      if (reportKey) {
        return (
          <Fragment key={`report-link-${reportKey}`}>
            -{" "}
            <ButtonBase
              onClick={() => onNavigate(reportKey, monthValue)}
              sx={{
                color: "primary.main",
                textDecoration: "underline",
                verticalAlign: "baseline",
                font: "inherit",
              }}
            >
              {trimmed}
            </ButtonBase>
          </Fragment>
        );
      }
    } else if (part === "reset your password") {
      return (
        <ButtonBase
          key="profile-link"
          onClick={onProfileNavigate}
          sx={{
            color: "primary.main",
            textDecoration: "underline",
            verticalAlign: "baseline",
            font: "inherit",
          }}
        >
          {part}
        </ButtonBase>
      );
    }

    return <span key={`text-${part.slice(0, 20)}`}>{part}</span>;
  });
};
